// @section 1 VPG All Threads — the browsing surface.
//
// Companion to Reply Review, and deliberately separate from it. Reply Review is a work queue:
// it shows only what needs answering, one thread at a time, and it is the only place a
// message can be sent. This screen is the opposite: every thread we have with anyone, on any
// location, in any state, so Patrick can go find a conversation ("what did we say to that
// agent in March?") without that lookup polluting the queue he is working down.
//
// Read-only on purpose. There is no Execute here. Sending stays behind the queue view, which
// has already excluded opt-outs and DND contacts.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listVpgAllThreads, getVpgAllFacets, getVpgThread,
  type VpgAllThreadRow, type VpgThreadDetail, type VpgFacets,
} from '../api';
import { useIsMobile } from '../lib/useIsMobile';

const PAGE = 100;

const STATE_TONE: Record<string, string> = {
  sent: '#14C1A8',
  handled_by_human: '#7c87a8',
  suppressed: '#7c87a8',
  dead: '#7c87a8',
  closed: '#7c87a8',
  failed: '#f87171',
  drafted: '#60a5fa',
  approved: '#f59e0b',
  none: '#f59e0b',
};

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function fmtDay(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

export default function VpgThreads() {
  const isMobile = useIsMobile();
  const px = isMobile ? 12 : 22;

  const [rows, setRows] = useState<VpgAllThreadRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [facets, setFacets] = useState<VpgFacets | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [persona, setPersona] = useState('');
  const [market, setMarket] = useState('');
  const [kind, setKind] = useState('');
  const [replyState, setReplyState] = useState('');
  const [sort, setSort] = useState<'recent' | 'oldest' | 'busiest'>('recent');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  const [selected, setSelected] = useState<{ locationId: string; contactId: string } | null>(null);
  const [detail, setDetail] = useState<VpgThreadDetail | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);

  // @block 1.1 Debounce the search box. The corpus is ~38K threads and the ILIKE runs across
  // body + name + phone + email, so firing on every keystroke would queue a scan per letter.
  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput); setSkip(0); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);

  const load = useCallback(() => {
    setErr(null);
    listVpgAllThreads({
      persona: persona || undefined,
      market: market || undefined,
      kind: kind || undefined,
      replyState: replyState || undefined,
      q: q || undefined,
      sort,
      take: PAGE,
      skip,
    })
      .then((r) => { setRows(r.rows); setTotal(r.total); })
      .catch((e) => setErr(String(e)));
  }, [persona, market, kind, replyState, q, sort, skip]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getVpgAllFacets().then(setFacets).catch(() => setFacets(null));
  }, []);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let live = true;
    setDetail(null); setDetailErr(null);
    getVpgThread(selected.locationId, selected.contactId)
      .then((d) => { if (live) setDetail(d); })
      .catch((e) => { if (live) setDetailErr(String(e)); });
    return () => { live = false; };
  }, [selected]);

  const messagesRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (detail && messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [detail]);

  const opts = (name: string) => facets?.facets[name] ?? [];
  const pageFrom = total === 0 ? 0 : skip + 1;
  const pageTo = Math.min(skip + PAGE, total);

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', minHeight: 0 }}>
      {/* @block 1.2 Filter bar */}
      <div style={{
        padding: `10px ${px}px`, borderBottom: '1px solid var(--t2-border)',
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <span style={{ color: 'var(--tx-3)', fontSize: 14 }}>
          {rows == null ? 'Loading…' : `${pageFrom.toLocaleString()}–${pageTo.toLocaleString()} of ${total.toLocaleString()} threads`}
        </span>

        <select value={persona} onChange={(e) => { setPersona(e.target.value); setSkip(0); }} style={selStyle}>
          <option value="">All personas</option>
          {opts('persona').map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.value} ({o.count})</option>)}
        </select>
        <select value={market} onChange={(e) => { setMarket(e.target.value); setSkip(0); }} style={selStyle}>
          <option value="">All accounts</option>
          {opts('market').map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.value} ({o.count})</option>)}
        </select>
        <select value={kind} onChange={(e) => { setKind(e.target.value); setSkip(0); }} style={selStyle}>
          <option value="">All kinds</option>
          {opts('kind').map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.value} ({o.count})</option>)}
        </select>
        <select value={replyState} onChange={(e) => { setReplyState(e.target.value); setSkip(0); }} style={selStyle}>
          <option value="">Any state</option>
          {opts('replyState').map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.value} ({o.count})</option>)}
        </select>
        <select value={sort} onChange={(e) => { setSort(e.target.value as typeof sort); setSkip(0); }} style={selStyle}>
          <option value="recent">Most recent activity</option>
          <option value="oldest">Oldest inbound</option>
          <option value="busiest">Most messages</option>
        </select>
        <input
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          placeholder="Search name, phone, email, message"
          style={{ ...selStyle, minWidth: 230 }}
        />

        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          <button
            onClick={() => setSkip((s) => Math.max(0, s - PAGE))}
            disabled={skip === 0}
            style={{ ...btnGhost, opacity: skip === 0 ? 0.4 : 1 }}
          >
            ← Prev
          </button>
          <button
            onClick={() => setSkip((s) => s + PAGE)}
            disabled={pageTo >= total}
            style={{ ...btnGhost, opacity: pageTo >= total ? 0.4 : 1 }}
          >
            Next →
          </button>
        </div>
      </div>

      {/* @block 1.3 List + conversation */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(300px, 400px) 1fr',
        minHeight: 0,
      }}>
        <div style={{
          overflow: 'auto', borderRight: isMobile ? 'none' : '1px solid var(--t2-border)',
          display: isMobile && selected ? 'none' : 'block',
        }}>
          {err && <div style={{ color: '#f87171', fontSize: 15, padding: 14 }}>{err}</div>}
          {rows == null && !err && (
            <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 20 }}>Loading threads…</div>
          )}
          {rows?.length === 0 && (
            <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 20 }}>
              No threads match those filters.
            </div>
          )}
          {rows?.map((r) => {
            const active = selected?.locationId === r.locationId && selected?.contactId === r.contactId;
            return (
              <button
                key={`${r.locationId}:${r.contactId}`}
                onClick={() => setSelected({ locationId: r.locationId, contactId: r.contactId })}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  padding: '10px 14px', border: 'none',
                  borderBottom: '1px solid var(--t2-border)',
                  borderLeft: active ? '3px solid var(--brand-3)' : '3px solid transparent',
                  background: active ? 'rgba(20,193,168,0.08)' : 'transparent',
                  color: 'inherit', font: 'inherit',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--tx-0)' }}>
                    {r.contactName || r.firstName || r.phone || r.contactId}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                    {fmtDay(r.lastInboundAt ?? r.lastOutboundAt)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--tx-3)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span>{r.market}</span>
                  <span style={{ color: STATE_TONE[r.replyState ?? ''] ?? 'var(--tx-3)' }}>· {r.replyState}</span>
                  {r.awaitingReply && <span style={{ color: '#f59e0b' }}>· awaiting</span>}
                  {r.dnd && <span style={{ color: '#f87171' }}>· DND</span>}
                  <span>· {r.inboundN}/{r.outboundN}</span>
                </div>
                <div style={{
                  fontSize: 14, color: 'var(--tx-2)', marginTop: 4,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {r.lastInboundBody}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {!selected && (
            <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 30 }}>
              Pick a thread to read the conversation.
            </div>
          )}
          {selected && detail == null && !detailErr && (
            <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 30 }}>Loading thread…</div>
          )}
          {detailErr && <div style={{ color: '#f87171', fontSize: 15, padding: 20 }}>{detailErr}</div>}

          {detail && (
            <>
              <div style={{ padding: `12px ${px}px`, borderBottom: '1px solid var(--t2-border)' }}>
                {isMobile && (
                  <button onClick={() => setSelected(null)} style={{ ...btnGhost, marginBottom: 6 }}>← Threads</button>
                )}
                <div style={{ fontSize: 21, fontWeight: 700, color: 'var(--tx-0)' }}>
                  {detail.thread.contactName || detail.thread.contactId}
                </div>
                <div style={{ fontSize: 14, color: 'var(--tx-3)', marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span>{detail.thread.market}</span>
                  <span>persona {detail.thread.persona ?? '—'}</span>
                  <span>{detail.thread.kind}</span>
                  <span style={{ color: STATE_TONE[detail.thread.replyState ?? ''] ?? 'var(--tx-3)' }}>
                    {detail.thread.replyState}
                  </span>
                  <span>{detail.thread.inboundN} in / {detail.thread.outboundN} out</span>
                  {detail.thread.phone && <span>{detail.thread.phone}</span>}
                  {detail.thread.repliedAt && <span>replied {fmtTime(detail.thread.repliedAt)}</span>}
                </div>
                {!detail.sendable && (
                  <div style={{ marginTop: 6, fontSize: 14, color: '#f87171' }}>
                    Opted out or DND. This contact is not replyable.
                  </div>
                )}
                {detail.decisions.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 13, color: 'var(--tx-3)' }}>
                    Last decision: {detail.decisions[0].action}
                    {detail.decisions[0].chosenVariant ? ` · ${detail.decisions[0].chosenVariant}` : ''}
                    {detail.decisions[0].customized ? ' (edited)' : ''}
                    {' · '}{fmtTime(detail.decisions[0].decidedAt)}
                    {detail.decisions[0].decidedBy ? ` · ${detail.decisions[0].decidedBy}` : ''}
                  </div>
                )}
              </div>

              <div ref={messagesRef} style={{ flex: 1, overflow: 'auto', padding: `12px ${px}px 24px` }}>
                {detail.messages.map((m, i) => {
                  const out = m.direction === 'outbound';
                  return (
                    <div key={m.id ?? i} style={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                      <div style={{
                        maxWidth: '78%', padding: '8px 11px', borderRadius: 8, fontSize: 15, lineHeight: 1.45,
                        background: out ? 'rgba(20,193,168,0.10)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${out ? 'rgba(20,193,168,0.25)' : 'var(--t2-border)'}`,
                        color: 'var(--tx-1)', whiteSpace: 'pre-wrap',
                      }}>
                        {m.body}
                        <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 4 }}>
                          {out ? 'us' : 'agent'} · {fmtTime(m.dateAdded)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {detail.messages.length === 0 && (
                  <div style={{ color: 'var(--tx-3)', fontStyle: 'italic' }}>No SMS history on file for this contact.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// @subsection 1.4 Local styles (matching the v5 canvas used across Atlas routes)
const selStyle: React.CSSProperties = {
  background: 'rgba(10,19,34,0.6)', color: 'var(--tx-1)',
  border: '1px solid var(--t2-border)', borderRadius: 4,
  padding: '5px 8px', fontSize: 14,
};

const btnGhost: React.CSSProperties = {
  background: 'none', border: '1px solid var(--t2-border)', borderRadius: 4,
  padding: '5px 10px', fontSize: 14, fontWeight: 600,
  color: 'var(--tx-2)', cursor: 'pointer',
};
